import type { PublicPriceTier } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { activeTierOf, parseOrderQty } from '@/entities/product';
import { SHIPPING_FEE_EXTRA_NOTE } from '@/entities/quote';
import { formatCount, formatKrw } from '@/shared/lib';

interface EstimatedTotalProps {
  /** minQty 오름차순. */
  readonly tiers: readonly PublicPriceTier[];
  readonly minOrderQty: number;
  /** 수량 입력 문자열 그대로. */
  readonly qtyDraft: string;
}

/**
 * 예상 합계 줄 — 입력 수량이 속한 구간 단가 × 수량을 오른쪽에 크게 보여 준다.
 * 수량이 잘못됐거나 최소 주문 수량 미만이면 합계 대신 최소 수량을 안내한다.
 */
export const EstimatedTotal = ({
  tiers,
  minOrderQty,
  qtyDraft,
}: EstimatedTotalProps): ReactElement => {
  const qty = parseOrderQty(qtyDraft);
  const activeTier = activeTierOf(tiers, qty);

  return (
    // 수량을 바꿀 때마다 합계가 바뀐다 — 화면낭독기에도 조용히 알린다.
    <div aria-live="polite" className="flex items-end justify-between gap-4 py-1">
      {qty !== null && activeTier !== null ? (
        <>
          <div className="space-y-0.5 text-sm">
            <p className="font-semibold">{formatCount(qty)}개 기준 예상 합계</p>
            <p className="text-muted-foreground tabular-nums">
              개당 {formatKrw(activeTier.unitPrice)} · 부가세 포함
            </p>
            <p className="text-xs text-muted-foreground">{SHIPPING_FEE_EXTRA_NOTE}</p>
          </div>
          <p className="text-[1.75rem] leading-none font-bold tracking-[-0.02em] tabular-nums">
            {formatKrw(qty * activeTier.unitPrice)}
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          최소 주문 수량은 {formatCount(minOrderQty)}개입니다.
        </p>
      )}
    </div>
  );
};
